import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, takeUntil, tap } from 'rxjs';

import { DcIcons } from '../../../ui/app-dc.icons';
import {
	bulkDelete,
	bulkDeleteSuccess,
	createDatablocksFromEntities,
	createDatablocksFromEntitiesSuccess,
	deleteEntity,
	deleteEntitySuccess,
	EntitySelector,
	persistenceActionsAccepted,
	persistEntities,
	repersistEntities,
	unpersistEntities,
} from '../../store';

export enum EntityAction {
	Persist = 'persist',
	Repersist = 'repersist',
	Depersist = 'depersist',
	Delete = 'delete',
	CreateDatablock = 'create-datablock',
}

export interface IEntityActionModalData {
	entityId: number | Array<number>;
	element: string;
	action: EntityAction;
}
@Component({
	selector: 'app-entity-action-popup',
	templateUrl: './entity-action-popup.component.html',
	styleUrls: ['./entity-action-popup.component.scss'],
})
export class EntityActionPopupComponent<
	T extends IEntityActionModalData
> extends DcBaseComponent {
	public readonly Icons = DcIcons;
	public vo$: Observable<{
		element: string;
		entityId: number | Array<number>;
		isMultipleElement: boolean;
		action: EntityAction;
		isActionInProgress: boolean;
	}>;
	public confirmDeletion = false;

	protected readonly DcIcons = DcIcons;
	protected readonly EntityAction = EntityAction;
	public constructor(
		private readonly modalService: AppModalService,
		private readonly store: Store,
		private readonly entitySelector: EntitySelector,
		private readonly actions$: Actions,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'entity-action';

		this.vo$ = combineAll({
			element: this.modalData.element,
			isMultipleElement:
				Array.isArray(this.modalData.entityId) && this.modalData.element === '',
			entityId: this.modalData.entityId,
			action: this.modalData.action,
			isActionInProgress: this.entitySelector.getActionInProgress$(),
		});

		this.handleSubscriptions();
	}

	public confirmAction(): void {
		const entityIds = this.entityIds();
		switch (this.modalData.action) {
			case EntityAction.Persist:
				return this.store.dispatch(
					persistEntities({
						entityIds,
					})
				);
			case EntityAction.Repersist:
				return this.store.dispatch(
					repersistEntities({
						entityIds,
					})
				);
			case EntityAction.Depersist:
				return this.store.dispatch(
					unpersistEntities({
						entityIds,
					})
				);
			case EntityAction.CreateDatablock:
				return this.store.dispatch(
					createDatablocksFromEntities({
						entityId: entityIds,
					})
				);
			case EntityAction.Delete:
				if (entityIds.length === 1) {
					return this.store.dispatch(
						deleteEntity({
							entityId: entityIds[0],
						})
					);
				}
				return this.store.dispatch(
					bulkDelete({
						entityId: entityIds,
					})
				);
		}
	}

	private entityIds(): Array<number> {
		const { entityId } = this.modalData;
		return Array.isArray(entityId) ? entityId : [entityId];
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					persistenceActionsAccepted,
					deleteEntitySuccess,
					bulkDeleteSuccess,
					createDatablocksFromEntitiesSuccess,
				),
				tap(() => this.modalService.closeActiveModal())
			)
			.subscribe();
	}
}
